import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { REMINDERS, RmMessagesView, RmTestView, sectionById } from '@elva/ui';
import type { RmSection } from '@elva/ui';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';

/**
 * The reminders editors (amendment 25 §1). **Four kinds of section, one pair of
 * pages** — before the visit, one procedure's post-op checks, the missed-visit
 * follow-up and the review ask. The missed and review sections are the same
 * editor with `maxSteps: 1`, which is why there is no third component for them.
 *
 * The edited section lives in a module-level store for the same reason the recall
 * draft does: React swaps props without remounting at the same position in the
 * tree, so state held in one page is lost on the hop to the other.
 */
const store: { sections: Record<string, RmSection> } = { sections: {} };

function load(key: string): RmSection | undefined {
  const held = store.sections[key];
  if (held !== undefined) return held;
  const fresh = sectionById(key);
  if (fresh === undefined) return undefined;
  store.sections[key] = fresh;
  return fresh;
}

function useSection(key: string) {
  const [section, setLocal] = useState<RmSection | undefined>(() => load(key));
  const update = useCallback(
    (next: RmSection) => {
      store.sections[next.key] = next;
      setLocal(next);
    },
    [],
  );
  return [section, update] as const;
}

/**
 * `before` · `missed` · `review` are top-level; a procedure is `after/<key>`. The
 * component below is mounted for both, so the key is assembled from whichever
 * params are present.
 */
function useSectionKey(): string {
  const { section, procedure } = useParams();
  return procedure ?? section ?? 'before';
}

function editorBase(key: string): string {
  const path = key === 'before' || key === 'missed' || key === 'review' ? key : `after/${key}`;
  return `/settings/ai/reminders/${path}`;
}

export function RemindersMessagesPage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const key = useSectionKey();
  const [section, setSection] = useSection(key);
  // A fix link on the test page names a step, and `?step=` is how it survives the
  // navigation — without it the page opens step 1 whatever the link said.
  const [params] = useSearchParams();

  if (section === undefined) return <Navigate to="/settings/ai/reminders" replace />;

  return (
    <RmMessagesView
      section={section}
      initialStepId={params.get('step') ?? undefined}
      onChange={setSection}
      onBack={() => {
        void navigate('/settings/ai/reminders');
      }}
      onContinue={() => {
        void navigate(`${editorBase(key)}/test`);
      }}
      onOpenAppointmentReasons={() => {
        void navigate('/settings/appointment-reasons');
      }}
    />
  );
}

export function RemindersTestPage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const key = useSectionKey();
  const [section] = useSection(key);

  if (section === undefined) return <Navigate to="/settings/ai/reminders" replace />;

  return (
    <RmTestView
      section={section}
      onBack={() => {
        void navigate(`${editorBase(key)}/messages`);
      }}
      onEditStep={(stepId) => {
        void navigate(`${editorBase(key)}/messages?step=${stepId}`);
      }}
      onSave={() => {
        void navigate('/settings/ai/reminders');
      }}
    />
  );
}

/** `/settings/ai/reminders/<section>` on its own opens its Messages step. */
export function RemindersEditorRedirect() {
  const key = useSectionKey();
  return <Navigate to={`${editorBase(key)}/messages`} replace />;
}

/** Exported for the acceptance harness: every section has an editor route. */
export const REMINDER_SECTION_KEYS = REMINDERS.sections.map((s) => s.key);
